// src/components/ReminderCard.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  getKind,
  summarize,
} from "../utils/reminderSchedule";
import { MdAutoAwesome, MdOutlineNotifications, MdOutlineQuestionAnswer } from "react-icons/md";
import { GrGroup } from "react-icons/gr";
import { IoCalendarOutline } from "react-icons/io5";
import { answerLabel } from "./queries/QueryAnswersOverlay";


function cls(...a) { return a.filter(Boolean).join(" "); }

export default function ReminderCard({
  reminder,
  onEdit,
  onDelete,
  isGlobal = false,
  canEdit = true,
  canDelete = canEdit,
  isActivation = false,
  onShowAnswers,
  showLatestAnswer = true,
}) {
  const r = reminder;
  const isQuery = !!r.is_query;
  const itemName = isActivation ? "Aktivierung" : isQuery ? "Abfrage" : "Erinnerung";
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const parsedNextDt = r.next_occurrence_at ? new Date(r.next_occurrence_at) : null;
  const nextDt = parsedNextDt && !Number.isNaN(parsedNextDt.getTime()) ? parsedNextDt : null;

  // Event detection + effective flags
  const isEvent = ((r.entry_type || r.type || "") + "").toUpperCase() === "EVENT";
  const isFamilyReminder = r.source === "family_app";
  const effectiveIsGlobal = isGlobal || isEvent;      // show badge for events too
  const effectiveCanEdit  = canEdit && !isEvent;      // lock editing for events
  const effectiveCanDelete = canDelete && !!onDelete && !isEvent;
  const disabledActionTitle = isEvent
    ? "Globale Events können nur im Kalender bearbeitet werden"
    : isFamilyReminder
      ? "Diese Erinnerung wurde von einem Familienmitglied erstellt"
      : "Allgemeine Erinnerung – bitte in allgemeinen Erinnerungen bearbeiten";

  useEffect(() => {
    function onDocClick(e) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    function onKey(e) { if (e.key === "Escape") setMenuOpen(false); }
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const k = getKind(r);
  const kindLabel =
    k === "one_off" ? "Einmalig" :
    k === "daily"   ? "Routine"  :
    k === "weekly"  ? "Routine"  :
    k === "monthly" ? "Routine"  : "—";

  return (
    <li className="rounded-2xl bg-white shadow-md border border-black/5 p-3">
      {/* Header */}
      <div className="mb-2 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <span className="text-4xl">
            {isEvent ? <IoCalendarOutline /> : isActivation ? <MdAutoAwesome /> : isQuery ? <MdOutlineQuestionAnswer /> : <MdOutlineNotifications />}
          </span>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold text-gray-900">{r.title}</span>
              <span
                className={cls(
                  "inline-block h-2.5 w-2.5 rounded-full",
                  r.is_active ? "bg-green-500" : "bg-gray-400"
                )}
                title={r.is_active ? "aktiv" : "inaktiv"}
              />
            </div>
            {isQuery ? (
              <div className="mt-0.5 text-sm text-gray-600 line-clamp-2">„{r.query_config?.question}“</div>
            ) : r.description && (
              <div className="mt-0.5 text-sm text-gray-600 line-clamp-2">{r.description}</div>
            )}
          </div>
        </div>

        {/* Right side: Global badge + kebab */}
        <div className="relative flex items-center gap-1" ref={menuRef}>
          {effectiveIsGlobal && (
            <span
              title={isEvent ? "Kalender-Event (global verwaltet)" : `Globale ${itemName}`}
              className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-2 py-[2px] text-[11px] font-medium text-blue-700"
            >
              <GrGroup className="text-base" />
              Global
            </span>
          )}

          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
            className="rounded-full p-1.5 text-gray-600 hover:bg-gray-100"
            title="Aktionen"
          >
            ⋮
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 z-10 mt-1 w-36 overflow-hidden rounded-lg border border-black/10 bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                role="menuitem"
                onClick={() => { if (effectiveCanEdit) { setMenuOpen(false); onEdit?.(r); } }}
                disabled={!effectiveCanEdit}
                className={cls(
                  "block w-full px-3 py-2 text-left text-sm",
                  effectiveCanEdit ? "hover:bg-gray-50" : "opacity-50 cursor-not-allowed"
                )}
                title={
                  effectiveCanEdit
                    ? "Bearbeiten"
                    : disabledActionTitle
                }
              >
                Bearbeiten
              </button>
              <button
                role="menuitem"
                onClick={() => { if (effectiveCanDelete) { setMenuOpen(false); onDelete?.(r); } }}
                disabled={!effectiveCanDelete}
                className={cls(
                  "block w-full px-3 py-2 text-left text-sm",
                  effectiveCanDelete ? "text-red-700 hover:bg-red-50" : "text-red-700 opacity-50 cursor-not-allowed"
                )}
                title={effectiveCanDelete ? "Löschen" : disabledActionTitle}
              >
                Löschen
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Info rows */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 rounded-xl bg-[#e8f3ff] px-3 py-2 text-sm">
          <span className="text-gray-600">Typ</span>
          <span className="ml-auto font-semibold text-gray-900">
            {kindLabel}{effectiveIsGlobal ? " • Global" : ""}
          </span>
        </div>

        <div className="flex items-center gap-2 rounded-xl bg-[#e8f3ff] px-3 py-2 text-sm">
          <span className="text-gray-600">Wann?</span>
          <span className="ml-auto font-semibold text-gray-900">{summarize(r)}</span>
        </div>

        <div className="flex items-center gap-2 rounded-xl bg-[#e8f3ff] px-3 py-2 text-sm">
          <span className="text-gray-600">nächste {itemName}</span>
          <span
            className="ml-auto font-semibold text-gray-900"
            title={nextDt ? nextDt.toLocaleString() : ""}
          >
            {r.next_occurrence_label || "Kein nächster Zeitpunkt"}
          </span>
        </div>

        {isQuery && showLatestAnswer && (
          <div className="flex items-center gap-2 rounded-xl bg-[#e8f3ff] px-3 py-2 text-sm">
            <span className="text-gray-600">Letzte Antwort</span>
            <span className="ml-auto font-semibold text-gray-900">
              {r.latest_answer ? answerLabel(r.latest_answer) : "Noch nicht gefragt"}
            </span>
          </div>
        )}
      </div>

      {isQuery && onShowAnswers && (
        <button
          type="button"
          onClick={() => onShowAnswers(r)}
          className="mt-3 w-full rounded-xl border border-blue-200 bg-white px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50"
        >
          Antworten ansehen
        </button>
      )}
    </li>
  );
}
